import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Head } from 'vite-react-ssg'
import { getCanonicalUrl } from '../utils/canonical'
import {
  CheckCircle,
  Shield,
  Zap,
  ChevronDown,
  ChevronUp,
  // ExternalLink
} from 'lucide-react'
import InlineScanSection from '../components/InlineScanSection'

export default function WcagValidatorPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null)

  const wcagChecks = [
    'Alternative text for images (1.1.1)',
    'Color contrast ratios (1.4.3)',
    'Programmatic form labels (3.3.2)',
    'Logical focus order and visible focus indicators (2.4.7)',
    'Correct use of ARIA roles for dynamic content'
  ]

  const commonIssues = [
    {
      issue: 'Missing alt text for images',
      description: 'Violates a fundamental principle for screen reader users.'
    },
    {
      issue: 'Insufficient color contrast',
      description: 'Makes content unreadable for users with low vision.'
    },
    {
      issue: 'Inaccessible form fields',
      description: 'Occurs when inputs lack proper labels for assistive technology.'
    },
    {
      issue: 'Keyboard traps / missing focus indicators',
      description: 'Prevents keyboard-only users from navigating the site.'
    },
    {
      issue: 'Improper heading structure',
      description: 'Confuses users who rely on headings to understand page layout.'
    },
    {
      issue: 'Missing page titles',
      description: 'Disorients users about their location on the website.'
    }
  ]

  const complianceStandards = [
    {
      standard: 'WCAG 2.0 AA',
      applicableIn: 'Canada (Ontario)',
      primaryAudience: 'AODA: Public & Private Sector'
    },
    {
      standard: 'WCAG 2.1 AA',
      applicableIn: 'Canada (Federal)',
      primaryAudience: 'Accessible Canada Act: Federally Regulated Orgs'
    },
    {
      standard: 'WCAG 2.x AA',
      applicableIn: 'USA (via courts)',
      primaryAudience: 'ADA: Public & Commercial'
    },
    {
      standard: 'WCAG 2.0 AA',
      applicableIn: 'USA (Federal)',
      primaryAudience: 'Section 508: Federal Agencies'
    },
    {
      standard: 'WCAG 2.1 AA',
      applicableIn: 'European Union',
      primaryAudience: 'EN 301 549 & EAA: Public & Private Sector'
    }
  ]

  const faqs = [
    {
      question: 'What is a WCAG checker?',
      answer: 'A WCAG checker is an automated tool that scans a website\'s code against the Web Content Accessibility Guidelines. It identifies technical errors that could create barriers for people with disabilities. Use the free accessibility checker on achecker.ca to get an instant report.'
    },
    {
      question: 'Does the achecker.ca WCAG validator test against WCAG 2.2 AA?',
      answer: 'Yes. Our WCAG validator uses test rules based on the latest W3C standard, WCAG 2.2, and focuses on Level A and AA criteria. This ensures your audit is aligned with current legal and technical expectations.'
    },
    {
      question: 'What\'s the difference between WCAG 2.0, 2.1, and 2.2?',
      answer: 'Each version is backward-compatible but adds new success criteria. WCAG 2.1 added criteria for mobile and low-vision users, while WCAG 2.2 introduced new requirements for focus appearance, dragging movements, and more. Testing against 2.2 is the best practice.'
    },
    {
      question: 'Do I need AAA compliance?',
      answer: 'For most websites, WCAG AA compliance is the target goal and the level required by law. Level AAA is a higher standard that is often difficult to achieve for all content but can be a goal for specific parts of a website dedicated to accessibility.'
    },
    {
      question: 'Does a 100% score equal ADA compliance?',
      answer: 'No. A 100% score from any automated WCAG checker means no machine-detectable errors were found. Full ADA compliance considers the overall user experience, which requires manual testing by humans. However, a high score is a critical first step.'
    },
    {
      question: 'What issues can\'t be fully automated?',
      answer: 'Automated tools struggle with context. They cannot reliably test the logical order of keyboard navigation, the quality of alt text, the clarity of link text, or whether complex interactions are intuitive for screen reader users.'
    },
    {
      question: 'Is the WCAG compliance checker on achecker.ca free?',
      answer: 'Yes. The WCAG validator available on our website is free for on-demand, single-page scans.'
    },
    {
      question: 'How often should I run a WCAG audit?',
      answer: 'You should run a WCAG audit whenever you make significant changes to your website\'s design, code, or content. Regular, automated scanning helps catch accessibility issues before they impact your users.'
    },
    {
      question: 'Can I use results from achecker.ca in accessibility statements?',
      answer: 'Results from our WCAG validator are a great way to show your commitment to accessibility and to document your initial findings. However, a formal accessibility statement or VPAT should be based on a comprehensive audit that includes both automated and manual testing.'
    },
    {
      question: 'Is achecker.ca an official W3C validator?',
      answer: 'No, achecker.ca is an independent tool. There is no "official" W3C validator for entire websites. Our tool is built using open standards and rules that are directly based on the W3C\'s WCAG documentation to provide an accurate and reliable audit.'
    }
  ]

  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index)
  }

  return (
    <>
      <Head>
        <title>Free WCAG Checker & Validator (2.2 AA) – achecker.ca</title>
        <meta name="description" content="Run a free WCAG compliance checker on achecker.ca. Instant WCAG 2.2 AA audit, Accessibility Score, and prioritized fixes for website accessibility." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={getCanonicalUrl('/wcag-validator')} />
      </Head>

      <div className="space-y-12">
        {/* Hero Section */}
        <section className="text-center py-12 bg-gradient-to-br from-green-50 to-emerald-100 rounded-2xl">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            Free WCAG Checker & Validator – Instant WCAG 2.2 AA Audit with Accessibility Score
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-4xl mx-auto">
            Run a free WCAG check on your website with the validator tool from achecker.ca. In seconds, our <strong>WCAG compliance checker</strong> performs a detailed audit based on the latest Web Content Accessibility Guidelines (WCAG 2.2 AA), giving you an instant Accessibility Score and a prioritized list of fixes. Use the <strong>achecker.ca WCAG Validator</strong> to ensure your site meets global standards.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="#scan-section"
              className="inline-flex items-center justify-center px-6 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors"
              onClick={(e) => {
                e.preventDefault()
                document.getElementById('scan-section')?.scrollIntoView({ behavior: 'smooth' })
              }}
            >
              <Zap className="w-5 h-5 mr-2" />
              Start WCAG Check
            </a>
            <Link
              to="/"
              className="inline-flex items-center justify-center px-6 py-3 bg-white text-gray-700 rounded-lg font-medium border border-gray-300 hover:bg-gray-50 transition-colors"
            >
              <Shield className="w-5 h-5 mr-2" />
              General Accessibility Checker
            </Link>
          </div>
        </section>

        {/* Inline Scan Section */}
        <InlineScanSection />

        {/* Free WCAG Checker */}
        <section>
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Free WCAG Checker on achecker.ca</h2>
          <div className="bg-white rounded-lg p-8 shadow-sm border border-gray-200">
            <p className="text-lg text-gray-700 mb-6">
              You can run a WCAG-based accessibility audit in seconds. The Web Content Accessibility Guidelines are organized into three levels of conformance: Level A (the minimum), Level AA (the standard for most laws and organizations), and Level AAA (the highest level). The <strong>WCAG checker by achecker.ca</strong> focuses on A and AA criteria, as these are the benchmarks for global compliance.
            </p>
          </div>
        </section>

        {/* How the Validator Works */}
        <section>
          <h2 className="text-3xl font-bold text-gray-900 mb-6">How the achecker.ca WCAG Validator Works</h2>
          <div className="bg-white rounded-lg p-8 shadow-sm border border-gray-200">
            <p className="text-lg text-gray-700 mb-6">
              Our automated <strong>WCAG validator</strong> scans the code of your provided URL. It identifies issues and maps them directly to specific WCAG 2.2 success criteria. You receive an overall Accessibility Score and a clear report detailing what was checked, including:
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                {wcagChecks.map((check, index) => (
                  <div key={index} className="flex items-start space-x-3 mb-3">
                    <CheckCircle className="w-6 h-6 text-green-500 mt-0.5 flex-shrink-0" />
                    <p className="text-gray-700">{check}</p>
                  </div>
                ))}
              </div>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-yellow-800 text-sm">
                  <strong>Important:</strong> It's important to remember that automation has its limits. A full WCAG audit always requires manual verification to assess user experience and context-dependent issues.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Why WCAG Compliance Matters */}
        <section>
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Why WCAG Compliance Matters</h2>
          <div className="bg-white rounded-lg p-8 shadow-sm border border-gray-200">
            <p className="text-lg text-gray-700 mb-6">
              WCAG is the global benchmark for web accessibility, developed by the World Wide Web Consortium (W3C). These guidelines are the technical standard referenced by most accessibility legislation around the world, including <strong>AODA and the Accessible Canada Act in Canada</strong>, the ADA in the USA, Section 508, and the EU's EN 301 549. The latest version, WCAG 2.2, was published in October 2023.
            </p>
          </div>
        </section>

        {/* WCAG Versions */}
        <section>
          <h2 className="text-3xl font-bold text-gray-900 mb-6">WCAG 2.2 vs 2.1 vs 2.0 – What Changed?</h2>
          <div className="bg-white rounded-lg p-8 shadow-sm border border-gray-200">
            <p className="text-lg text-gray-700 mb-6">
              Each new version of WCAG builds upon the previous one by adding new success criteria to address modern web challenges and user needs. WCAG 2.1 added criteria for mobile accessibility and users with low vision or cognitive disabilities. WCAG 2.2 adds further criteria, making the standard even more robust. Scanning against the newest version with a <strong>WCAG 2.2 checker</strong> ensures your website is aligned with current best practices.
            </p>
          </div>
        </section>

        {/* Common WCAG Issues */}
        <section>
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Common WCAG Issues Found by achecker.ca</h2>
          <div className="bg-white rounded-lg p-8 shadow-sm border border-gray-200">
            <p className="text-gray-700 mb-6">
              Our free WCAG check is excellent at finding common, high-impact accessibility barriers. Here are some of the most frequent issues our tool identifies:
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              {commonIssues.map((issue, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">{issue.issue}</h4>
                  <p className="text-gray-600 text-sm">{issue.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Compliance Mapping */}
        <section>
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Map Your WCAG Score to Global Compliance</h2>
          <p className="text-gray-700 mb-6">
            While laws vary, WCAG Level AA is the common denominator for compliance across most major jurisdictions. <strong>achecker.ca is your free WCAG compliance checker</strong> to help you meet these obligations.
          </p>
          
          <div className="overflow-x-auto">
            <table className="w-full bg-white rounded-lg shadow-sm border border-gray-200">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-900">Standard</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-900">Applicable In</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-900">Primary Audience</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {complianceStandards.map((item, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{item.standard}</td>
                    <td className="px-6 py-4 text-sm text-gray-700">{item.applicableIn}</td>
                    <td className="px-6 py-4 text-sm text-gray-700">{item.primaryAudience}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* FAQ Section */}
        <section>
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Frequently Asked Questions (FAQ)</h2>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200">
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50 transition-colors"
                >
                  <h3 className="text-lg font-medium text-gray-900">{faq.question}</h3>
                  {openFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-gray-500" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-500" />
                  )}
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-4">
                    <p className="text-gray-700">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* CTA Section */}
        <section className="text-center py-12 bg-gradient-to-r from-green-600 to-emerald-600 rounded-2xl text-white">
          <h2 className="text-3xl font-bold mb-4">
            Ready to Run Your WCAG Compliance Check?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Get an instant WCAG 2.2 AA audit with our free validator and start improving your website's accessibility
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="#scan-section"
              className="inline-flex items-center justify-center px-8 py-3 bg-white text-green-600 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
              onClick={(e) => {
                e.preventDefault()
                document.getElementById('scan-section')?.scrollIntoView({ behavior: 'smooth' })
              }}
            >
              <Zap className="w-5 h-5 mr-2" />
              Start WCAG Validator
            </a>
            <Link
              to="/ada-compliance-checker"
              className="inline-flex items-center justify-center px-8 py-3 bg-green-700 text-white rounded-lg font-semibold hover:bg-green-800 transition-colors border border-green-500"
            >
              <Shield className="w-5 h-5 mr-2" />
              ADA Compliance Checker
            </Link>
          </div>
        </section>
      </div>
    </>
  )
}
